import React, { memo } from "react";
import { DateField as AntdDateField } from "@refinedev/antd";
import { Badge as AntdBadge } from "antd";
import {
  CalendarOutlined,
  DollarOutlined,
  IdcardOutlined,
  NumberOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { formatDate } from "../components/constants";
import { paymentStatusColors } from "../../utils/paymentStatus.utils";
import { INVOICE_STATUS_NAMES } from "../../utils/invoiceStatus";
import { ColumnType } from "antd/es/table";
import { Row } from "../components/interface";
import { amountListFormatter } from "../../utils/list.utils";
import { DownloadAttachmentsButton } from "../components/downloadAttachmentsButton";
import { cornerBadgeOnCell, NewTag } from "../components/newTag";

const DateField = memo(AntdDateField);
const Badge = memo(AntdBadge);

const IdHeaderCell = memo(() => (
  <span className={"column-header"}>
    <IdcardOutlined />
    &nbsp;ID
  </span>
));
IdHeaderCell.displayName = "IdHeaderCell";

const OrderIdHeaderCell = memo(() => (
  <span className={"column-header"}>
    <NumberOutlined />
    &nbsp;Trans. Ref.
  </span>
));
OrderIdHeaderCell.displayName = "RequestIdHeaderCell";

const CustomerHeaderCell = memo(() => (
  <span className={"column-header"}>
    <UserOutlined />
    &nbsp;Customer
  </span>
));
CustomerHeaderCell.displayName = "CustomerHeaderCell";

const RemitterHeaderCell = memo(() => (
  <span className={"column-header"}>
    <UserOutlined />
    &nbsp;Remitter
  </span>
));
RemitterHeaderCell.displayName = "RemitterHeaderCell";

const ServiceProviderHeaderCell = memo(() => (
  <span className={"column-header"}>
    <UserOutlined />
    &nbsp;Service provider
  </span>
));
ServiceProviderHeaderCell.displayName = "ServiceProviderHeaderCell";

const StatusHeaderCell = memo(() => (
  <span className={"column-header"}>
    <SyncOutlined />
    &nbsp;Status
  </span>
));
StatusHeaderCell.displayName = "StatusHeaderCell";

const DateHeaderCell = memo(() => (
  <span className={"column-header"}>
    <CalendarOutlined />
    &nbsp;Date
  </span>
));
DateHeaderCell.displayName = "DateHeaderCell";

const CurrencyHeaderCell = memo(() => (
  <span className={"column-header"}>
    <DollarOutlined />
    &nbsp;Currency
  </span>
));
CurrencyHeaderCell.displayName = "CurrencyHeaderCell";

const CurrencyAmountHeaderCell = memo(() => (
  <span className={"column-header"}>
    <DollarOutlined />
    &nbsp;Amount
  </span>
));
CurrencyAmountHeaderCell.displayName = "CurrencyAmountHeaderCell";

const CreatedAtHeaderCell = memo(() => (
  <span className={"column-header"}>
    <CalendarOutlined />
    &nbsp;Created
  </span>
));
CreatedAtHeaderCell.displayName = "CreatedAtHeaderCell";

const renderDateCell = (value: string) => {
  return <DateField value={value} format={formatDate} />;
};

const renderStatusCell = (value: string) => {
  return (
    <Badge
      color={paymentStatusColors[value]}
      text={INVOICE_STATUS_NAMES[value]}
    />
  );
};

export const getColumns = (newViewedIds: number[] = []): ColumnType<Row>[] => [
  {
    dataIndex: "id",
    sorter: true,
    title: <IdHeaderCell />,
  },
  {
    dataIndex: ["paymentOrder", "id"],
    title: <OrderIdHeaderCell />,
  },
  {
    dataIndex: ["paymentOrder", "customer", "fullName"],
    title: <CustomerHeaderCell />,
  },
  {
    dataIndex: ["paymentOrder", "remitter", "name"],
    sorter: true,
    title: <RemitterHeaderCell />,
  },
  {
    dataIndex: ["paymentOrder", "serviceProvider", "name"],
    sorter: true,
    title: <ServiceProviderHeaderCell />,
  },
  {
    dataIndex: "status",
    title: <StatusHeaderCell />,
    render: renderStatusCell,
    sorter: true,
  },
  {
    dataIndex: ["paymentOrder", "dateOfTransaction"],
    title: <DateHeaderCell />,
    sorter: true,
    render: renderDateCell,
  },
  {
    dataIndex: ["paymentOrder", "totalReceiveAmount"],
    title: <CurrencyAmountHeaderCell />,
    render: amountListFormatter,
  },
  {
    dataIndex: ["paymentOrder", "receiveCurrency", "symbol"],
    title: <CurrencyHeaderCell />,
  },
  {
    dataIndex: ["createdAt"],
    title: <CreatedAtHeaderCell />,
    sorter: true,
    render: (value: string) => <DateField value={value} format={formatDate} />,
  },
  {
    dataIndex: "documentName",
    onCell: cornerBadgeOnCell(),
    render: (value: string, data) => (
      <>
        {value && (
          <DownloadAttachmentsButton
            entity={"invoice_receivables"}
            id={data.id}
          />
        )}
        {data["rowNotViewed"] && !newViewedIds.includes(data.id as number) && (
          <NewTag />
        )}
      </>
    ),
  },
];
